import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useAlertEvents } from 'src/api/queries';
import { LoadFailure } from 'src/components/editorial/LoadFailure';
import { Badge } from 'src/components/ui/Badge';
import { Icon } from 'src/components/ui/Icon';
import { Skeleton } from 'src/components/ui/Skeleton';
import { ALERT_SURFACE_LABEL, ALERT_SURFACE_MEANING, CROSSING_LABEL } from 'src/lib/alerts';
import { formatClock, formatDateTime } from 'src/lib/time';
import type { EditorialAlertEvent } from 'src/types/editorial';

/**
 * Answers: what did this broadcast say that someone had asked to be told about, and when.
 *
 * Ordered by position in the broadcast rather than by when the pipeline noticed — the two differ
 * by minutes during a live capture and by hours on a reprocess, and the question being asked is
 * about the broadcast's own timeline. `occurredAt` stays on each entry, because the gap between
 * "said at 1:12:40" and "raised at 09:58" is what tells an editor how live the alert really was.
 * The rail is the landmark: entries hang off one line so the alerts read as a sequence, not as a
 * stack of cards.
 */
export function StreamAlertsWorkspace() {
  const { streamId = '' } = useParams<{ streamId: string }>();
  const query = useAlertEvents({ streamId, rowsPerPage: 200 });

  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'broadcast' | 'noticed'>('broadcast');

  const needle = search.trim().toLowerCase();
  const filtering = needle !== '' || sortBy !== 'broadcast';

  const events = useMemo(() => {
    if (!query.data) return [];
    const visible = needle
      ? query.data.events.filter(
          event =>
            (event.ruleName || event.ruleId).toLowerCase().includes(needle) ||
            event.matchedKeywords.some(keyword => keyword.toLowerCase().includes(needle)) ||
            (event.excerpt ?? '').toLowerCase().includes(needle)
        )
      : [...query.data.events];
    visible.sort(sortBy === 'noticed' ? byDetectionTime : byBroadcastPosition);
    return visible;
  }, [query.data, needle, sortBy]);

  // The section shell renders in every state — pending, error, empty — so the page never falls
  // back to a bare panel without its heading.
  return (
    <div className="page-medium stream-alerts">
      <header className="stream-alerts__head">
        <div className="stream-alerts__intro">
          <span className="stream-alerts__mark" aria-hidden="true">
            <Icon name="alerts" />
          </span>
          <div>
            <p className="stream-alerts__eyebrow">MONITOREO DE ESTA TRANSMISIÓN</p>
            <h2>Alertas</h2>
            <p className="stream-alerts__description">
              Reglas que encontraron una coincidencia. La hora indica cuándo se dijo; la fecha, cuándo lo detectó el
              sistema.
            </p>
          </div>
        </div>
        {query.data && (
          <div className="stream-alerts__summary" aria-live="polite">
            <strong>{filtering ? events.length : query.data.events.length}</strong>
            <span>{filtering ? `de ${query.data.events.length} coincidencias` : 'coincidencias'}</span>
          </div>
        )}
      </header>

      <section className="tx-filters" aria-label="Filtros de alertas">
        <div className="tx-field tx-search">
          <label htmlFor="stream-alerts-q">Buscar</label>
          <div className="tx-search-box">
            <span className="tx-search-icon" aria-hidden="true">
              <Icon name="search" />
            </span>
            <input
              id="stream-alerts-q"
              type="search"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Buscar regla, palabra o frase"
              autoComplete="off"
            />
          </div>
        </div>
        <div className="tx-field tx-field--select">
          <label htmlFor="stream-alerts-sort">Ordenar</label>
          <select
            id="stream-alerts-sort"
            value={sortBy}
            onChange={event => setSortBy(event.target.value as 'broadcast' | 'noticed')}
          >
            <option value="broadcast">Por la transmisión</option>
            <option value="noticed">Por detección</option>
          </select>
        </div>
        <button
          className="tx-clear"
          type="button"
          onClick={() => {
            setSearch('');
            setSortBy('broadcast');
          }}
          disabled={!filtering}
        >
          Limpiar
        </button>
      </section>

      {query.isPending ? (
        <Skeleton variant="rows" count={6} />
      ) : query.isError ? (
        <LoadFailure title="No se pudieron cargar las alertas" error={query.error} onRetry={query.refetch} />
      ) : query.data.events.length === 0 ? (
        <section className="stream-alerts-empty" aria-labelledby="stream-alerts-empty-title">
          <span className="stream-alerts-empty__mark" aria-hidden="true">
            <Icon name="alerts" />
          </span>
          <div className="stream-alerts-empty__content">
            <p className="stream-alerts__eyebrow">SIN COINCIDENCIAS</p>
            <h3 id="stream-alerts-empty-title">Esta transmisión no activó ninguna alerta</h3>
            <p className="stream-alerts-empty__description">
              Las reglas activas se revisan en el guion, las prenotas y los cruces de relevancia. Cuando alguna coincide,
              aparece aquí con su contexto y momento de detección.
            </p>
            <p className="stream-alerts-empty__note">
              Las reglas nuevas empiezan a evaluarse en los siguientes bloques; no generan alertas retroactivas.
            </p>
            <Link className="stream-alerts-empty__action" to="/alertas" data-nav>
              Administrar reglas <span aria-hidden="true">→</span>
            </Link>
          </div>
          <div className="stream-alerts-empty__status">
            <span aria-hidden="true">✓</span>
            Evaluación completa
          </div>
        </section>
      ) : events.length === 0 ? (
        <div className="stream-alerts__no-results">
          <Icon name="search" />
          <div>
            <strong>No encontramos coincidencias</strong>
            <p>Prueba otra palabra o restaura los filtros para ver todas las alertas.</p>
          </div>
          <button
            type="button"
            onClick={() => {
              setSearch('');
              setSortBy('broadcast');
            }}
          >
            Restaurar filtros
          </button>
        </div>
      ) : (
        <ol className="alert-timeline">
          {events.map(event => (
            <li key={event.eventId} className="alert-timeline__item">
              <AlertEntry event={event} />
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/** By when the pipeline noticed — "how live was the alert" reads in arrival order. */
function byDetectionTime(a: EditorialAlertEvent, b: EditorialAlertEvent): number {
  return (a.occurredAt ? Date.parse(a.occurredAt) : 0) - (b.occurredAt ? Date.parse(b.occurredAt) : 0);
}

/**
 * A relevance crossing has no position in the broadcast — it happened hours after it ended — so it
 * sorts to the end rather than to 0:00, where it would read as the first thing said.
 */
function byBroadcastPosition(a: EditorialAlertEvent, b: EditorialAlertEvent): number {
  const left = a.spokenSeconds ?? Number.MAX_SAFE_INTEGER;
  const right = b.spokenSeconds ?? Number.MAX_SAFE_INTEGER;
  if (left !== right) return left - right;
  return (a.occurredAt ? Date.parse(a.occurredAt) : 0) - (b.occurredAt ? Date.parse(b.occurredAt) : 0);
}

export function AlertEntry({
  event,
  showStream = false,
  unseenBefore,
}: {
  event: EditorialAlertEvent;
  showStream?: boolean;
  /** Set only on the Alertas history: an event newer than this cutoff hasn't been read yet. */
  unseenBefore?: number;
}) {
  const unseen = unseenBefore !== undefined && event.occurredAt !== null && Date.parse(event.occurredAt) > unseenBefore;

  return (
    <article className="alert-entry" data-surface={event.surface}>
      <header className="alert-entry__head">
        <span className="alert-entry__clock mono">{event.spokenSeconds === null ? '—' : formatClock(event.spokenSeconds)}</span>
        <span className="alert-entry__rule">{event.ruleName || event.ruleId}</span>
        <Badge tone="social" title={ALERT_SURFACE_MEANING[event.surface]}>
          {ALERT_SURFACE_LABEL[event.surface]}
        </Badge>
        {event.crossing && <span className="alert-entry__crossing">{CROSSING_LABEL[event.crossing]}</span>}
        {unseenBefore !== undefined && <Badge tone={unseen ? 'contradicted' : 'supported'}>{unseen ? 'por revisar' : 'visto'}</Badge>}
      </header>

      <ul className="alert-entry__keywords">
        {event.matchedKeywords.map(keyword => (
          <li key={keyword} className="topic-tag">
            {keyword}
          </li>
        ))}
      </ul>

      {event.excerpt && <p className="alert-entry__excerpt">{event.excerpt}</p>}

      <footer className="alert-entry__meta">
        <span>{formatDateTime(event.occurredAt)}</span>
        {event.relevanceScore !== null && (
          <span className="mono">
            {event.previousScore !== null ? `${Math.round(event.previousScore)} → ` : ''}
            {Math.round(event.relevanceScore)}
          </span>
        )}
        {showStream && event.streamId && (
          <Link to={`/stream/${encodeURIComponent(event.streamId)}/alertas`} data-nav>
            {event.channelName || event.streamId}
          </Link>
        )}
        {/* Where the alert came from, so it can be read in context rather than trusted as a line
            of text. A chunk alert points at the transcript; a story alert at the story. */}
        {!showStream && event.chunkId && (
          <Link to={`/stream/${encodeURIComponent(event.streamId)}/transcript`} data-nav>
            ver en el guion
          </Link>
        )}
        {!showStream && event.storyId && !event.chunkId && (
          <Link to={`/stream/${encodeURIComponent(event.streamId)}/stories`} data-nav>
            ver la historia
          </Link>
        )}
        <NotificationNote event={event} />
      </footer>
    </article>
  );
}

/** Whether anyone was mailed. Silence here would make a failed send indistinguishable from a
 *  dashboard-only rule, which is the difference between "nobody was told" and "nobody asked". */
function NotificationNote({ event }: { event: EditorialAlertEvent }) {
  if (event.notificationStatus === 'not_required') return null;

  if (event.notificationStatus === 'sent') {
    return <span className="alert-entry__mail">correo enviado {formatDateTime(event.notifiedAt)}</span>;
  }
  if (event.notificationStatus === 'failed') {
    return <span className="alert-entry__mail alert-entry__mail--failed">el correo falló: {event.notificationError || 'sin detalle'}</span>;
  }
  return <span className="alert-entry__mail">correo pendiente</span>;
}
